import '../assets/NcAppSettingsDialog-CW1IxPWr.css';
import debounce from "debounce";
import { resolveComponent, createBlock, createCommentVNode, openBlock, mergeProps, createSlots, withCtx, createElementVNode, renderSlot, createElementBlock, Fragment, renderList, withKeys, withModifiers, normalizeClass, toDisplayString, warn } from "vue";
import { useIsMobile } from "../composables/useIsMobile/index.mjs";
import { r as register, f as t45, a as t } from "./_l10n-CeXHJQB0.mjs";
import { N as NcDialog } from "./NcDialog-7-cxNVxV.mjs";
import { _ as _sfc_main$1 } from "./NcVNodes.vue_vue_type_script_lang-BqUHinRZ.mjs";
import { _ as _export_sfc } from "./_plugin-vue_export-helper-1tPrXgE0.mjs";
register(t45);
const _sfc_main = {
  name: "NcAppSettingsDialog",
  components: {
    NcDialog,
    NcVNodes: _sfc_main$1
  },
  provide() {
    return {
      registerSection: this.registerSection,
      unregisterSection: this.unregisterSection
    };
  },
  props: {
    /**
     * Determines the open / closed state of the modal
     */
    open: {
      type: Boolean,
      required: true
    },
    /**
     * Shows the navigation on desktop if true
     */
    showNavigation: {
      type: Boolean,
      default: false
    },
    /**
     * Selector for the popover container
     */
    container: {
      type: String,
      default: "body"
    },
    /**
     * Name of the settings
     */
    name: {
      type: String,
      default: ""
    },
    /**
     * Additional elements to add to the focus trap
     */
    additionalTrapElements: {
      type: Array,
      default: () => []
    }
  },
  emits: ["update:open"],
  setup() {
    return {
      isMobile: useIsMobile()
    };
  },
  data() {
    return {
      selectedSection: "",
      linkClicked: false,
      addedScrollListener: false,
      scroller: null,
      /**
       * Currently registered settings sections
       *
       * @type {{ id: string, name: string, icon?: import('vue').VNode[] }[]}
       */
      sections: []
    };
  },
  computed: {
    dialogProperties() {
      return {
        additionalTrapElements: this.additionalTrapElements,
        closeOnClickOutside: true,
        class: "app-settings",
        container: this.container,
        contentClasses: "app-settings__content",
        size: "large",
        name: this.name,
        navigationClasses: "app-settings__navigation"
      };
    },
    /**
     * Check if one or more navigation entries provide icons
     */
    hasNavigationIcons() {
      return this.sections.some(({ icon }) => !!icon);
    },
    hasNavigation() {
      if (this.isMobile || !this.showNavigation) {
        return false;
      } else {
        return true;
      }
    },
    settingsNavigationAriaLabel() {
      return t("Settings navigation");
    }
  },
  updated() {
    if (!this.$refs.settingsScroller) {
      return;
    }
    this.scroller = this.$refs.settingsScroller;
    if (!this.addedScrollListener) {
      this.scroller.addEventListener("scroll", this.handleScroll);
      this.addedScrollListener = true;
    }
  },
  methods: {
    /**
     * Called when a new section is registered
     *
     * @param {string} id The section ID
     * @param {string} name The section name
     * @param {import('vue').VNode[]|undefined} icon Optional icon component
     */
    registerSection(id, name, icon) {
      if (this.sections.some(({ id: otherId }) => id === otherId)) {
        throw new Error(`Duplicate section id found: ${id}. Settings navigation sections must have unique section ids.`);
      }
      if (this.sections.some(({ name: otherName }) => name === otherName)) {
        warn(`Duplicate section name found: ${name}. Settings navigation sections must have unique section names.`);
      }
      const newSections = [...this.sections, { id, name, icon }];
      this.sections = newSections.sort(({ id: idA }, { id: idB }) => {
        const indexOf = (id2) => this.$slots.default?.().indexOf((vnode) => vnode?.props?.id === id2) ?? -1;
        return indexOf(idA) - indexOf(idB);
      });
      if (this.sections.length === 1) {
        this.selectedSection = id;
      }
    },
    /**
     * Called when a section is unregistered to remove it from dialog
     *
     * @param {string} id The section ID
     */
    unregisterSection(id) {
      this.sections = this.sections.filter(({ id: otherId }) => id !== otherId);
      if (this.selectedSection === id) {
        this.selectedSection = this.sections[0]?.id ?? "";
      }
    },
    /**
     * Scrolls the content to the selected settings section.absolute
     *
     * @param {string} item the ID of the section
     */
    handleSettingsNavigationClick(item) {
      this.linkClicked = true;
      document.getElementById("settings-section_" + item).scrollIntoView({
        behavior: "smooth",
        inline: "nearest"
      });
      this.selectedSection = item;
      setTimeout(() => {
        this.linkClicked = false;
      }, 1e3);
    },
    handleCloseModal(isOpen) {
      if (isOpen) {
        return;
      }
      this.$emit("update:open", false);
      this.scroller.removeEventListener("scroll", this.handleScroll);
      this.addedScrollListener = false;
      this.scroller.scrollTop = 0;
    },
    handleScroll() {
      if (!this.linkClicked) {
        this.unfocusNavigationItem();
      }
    },
    /**
     * Remove selected section once the user starts scrolling
     *
     * @type {import('debounce').DebouncedFunction<() => void>}
     */
    unfocusNavigationItem: debounce(function() {
      this.selectedSection = "";
      if (document.activeElement.className.includes("navigation-list__link")) {
        document.activeElement.blur();
      }
    }, 300)
  }
};
const _hoisted_1 = {
  key: 0,
  class: "navigation-list"
};
const _hoisted_2 = ["aria-current", "href", "onClick", "onKeydown"];
const _hoisted_3 = {
  key: 0,
  class: "navigation-list__link-icon"
};
const _hoisted_4 = { class: "navigation-list__link-text" };
const _hoisted_5 = { ref: "settingsScroller" };
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_NcVNodes = resolveComponent("NcVNodes");
  const _component_NcDialog = resolveComponent("NcDialog");
  return $props.open ? (openBlock(), createBlock(_component_NcDialog, mergeProps({
    key: 0,
    "navigation-aria-label": $options.settingsNavigationAriaLabel
  }, $options.dialogProperties, { "onUpdate:open": $options.handleCloseModal }), createSlots({
    default: withCtx(() => [
      createElementVNode("div", _hoisted_5, [
        renderSlot(_ctx.$slots, "default", {}, void 0, true)
      ], 512)
    ]),
    _: 2
  }, [
    $options.hasNavigation ? {
      name: "navigation",
      fn: withCtx(({ isCollapsed }) => [
        !isCollapsed ? (openBlock(), createElementBlock("ul", _hoisted_1, [
          (openBlock(true), createElementBlock(Fragment, null, renderList($data.sections, (section) => {
            return openBlock(), createElementBlock("li", {
              key: section.id
            }, [
              createElementVNode("a", {
                "aria-current": `${section.id === $data.selectedSection}`,
                class: normalizeClass(["navigation-list__link", {
                  "navigation-list__link--active": section.id === $data.selectedSection,
                  "navigation-list__link--icon": $options.hasNavigationIcons
                }]),
                href: `#settings-section_${section.id}`,
                tabindex: "0",
                onClick: withModifiers(($event) => $options.handleSettingsNavigationClick(section.id), ["prevent"]),
                onKeydown: withKeys(($event) => $options.handleSettingsNavigationClick(section.id), ["enter"])
              }, [
                $options.hasNavigationIcons ? (openBlock(), createElementBlock("div", _hoisted_3, [
                  section.icon ? (openBlock(), createBlock(_component_NcVNodes, {
                    key: 0,
                    vnodes: section.icon
                  }, null, 8, ["vnodes"])) : createCommentVNode("", true)
                ])) : createCommentVNode("", true),
                createElementVNode("span", _hoisted_4, toDisplayString(section.name), 1)
              ], 42, _hoisted_2)
            ]);
          }), 128))
        ])) : createCommentVNode("", true)
      ]),
      key: "0"
    } : void 0
  ]), 1040, ["navigation-aria-label", "onUpdate:open"])) : createCommentVNode("", true);
}
const NcAppSettingsDialog = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render], ["__scopeId", "data-v-39ac847e"]]);
export {
  NcAppSettingsDialog as N
};
//# sourceMappingURL=NcAppSettingsDialog-GHmlZjop.mjs.map
